import { Company } from "@/sharedTypes";
import { baseApi } from "../baseApi";

const companyTag = "CompanyInfo";

export type CompanyUpdatePayload = Partial<
  Pick<
    Company,
    | "company_name"
    | "company_website"
    | "company_phone"
    | "country"
    | "sector_type"
    | "sector_model"
    | "company_language"
    | "max_concurrent_capacity"
  >
>;

export interface CompanyCreatePayload {
  company_name: string;
  company_website: string;
  company_phone: string;
  company_domain: string;
  country: string;
  sector_type: string;
  sector_model: string;
  max_concurrent_capacity?: number;
}

export interface CheckSubdomainResponse {
  message: string;
}

export interface CompaniesListResponse {
  items: Company[];
  pagination: {
    totalPages: number;
    pageIndex: number;
    totalItems: number;
    pageSize: number;
  };
}

export interface CompaniesListQuery {
  page?: number;
  pageSize?: number;
}

const companyApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCompanyDetails: builder.query<Company | null, void>({
      query: () => "/company",
      transformResponse: (response: any) => response?.data ?? response ?? null,
      providesTags: [companyTag],
    }),
    getAllCompanies: builder.query<CompaniesListResponse, CompaniesListQuery | void>({
      query: (params) => {
        const { page, pageSize } = params || {};
        const qs = new URLSearchParams();
        if (page) qs.set("page", String(page));
        if (pageSize) qs.set("pageSize", String(pageSize));
        const s = qs.toString();
        return `/company${s ? `?${s}` : ""}`;
      },
      transformResponse: (response: any): CompaniesListResponse => {
        const items: Company[] = Array.isArray(response)
          ? response
          : Array.isArray(response?.items)
            ? response.items
            : Array.isArray(response?.data?.items)
              ? response.data.items
              : Array.isArray(response?.data)
                ? response.data
                : [];
        return {
          items,
          pagination: response?.pagination ??
            response?.data?.pagination ?? {
              totalPages: 1,
              pageIndex: 1,
              totalItems: items.length,
              pageSize: items.length,
            },
        };
      },
      providesTags: [companyTag],
    }),
    updateCompany: builder.mutation<
      { data: Company; message: string },
      { company_id: number; data: CompanyUpdatePayload }
    >({
      query: ({ company_id, data }) => ({
        url: `/company/${company_id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: [companyTag],
    }),
    createCompany: builder.mutation<
      { data: Company; message: string },
      CompanyCreatePayload
    >({
      query: (data) => ({
        url: "/company/create",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [companyTag],
    }),
    checkCompanyDomain: builder.query<CheckSubdomainResponse, string>({
      query: (company_domain) => ({
        url: `/company/check-subdomain`,
        params: { company_domain },
      }),
    }),
  }),
});

export const {
  useGetCompanyDetailsQuery,
  useGetAllCompaniesQuery,
  useUpdateCompanyMutation,
  useCreateCompanyMutation,
  useLazyCheckCompanyDomainQuery,
} = companyApi;
